<template>
	<div class="schedule-tour" data-testid="schedule-tour">
		<div
			v-if="loading"
			class="d-flex align-items-center justify-content-center my-8 py-8"
			data-testid="loading"
		>
			<div class="spinner-border" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
		</div>
		<form v-if="!loading" data-testid="form" @submit="onSubmit">
			<div v-show="step === 1" data-testid="step-1">
				<h3 class="h6 mb-5">Select your date and time</h3>

				<DateSelector v-model="selectedDay" :days="days" />

				<div v-if="firstAvailableMsg" class="mb-3 p-3 bg-contrast-400">
					<p class="small fw-bold m-0" data-testid="first-available-times">
						The first available times are on {{ selectedDay?.dayObject?.format('MMMM D') }}.
					</p>
				</div>

				<TimeSlots v-model="selectedTimeslot" :time-slots="selectedDay?.timeSlots" />

				<hr />
			</div>

			<BookingSummary
				v-if="step !== 'Error'"
				v-model:step="step"
				:day="selectedDay?.dayObject"
				:time-slot="selectedTimeslot"
				:default-duration="defaultDuration"
			/>

			<div v-show="step === 2" data-testid="step-2">
				<hr class="my-3" />
				<h3 class="h6 mb-6">Confirm Your Booking</h3>

				<ContactForm>
					<template v-if="isEnhanced" #additionalFields>
						<EnhancedFields
							:membership-types="membershipTypes"
							:starting-price="startingPrice"
							:interests="interests"
						/>
					</template>
					<template #touText>
						{{ consentText }}
					</template>
					<template #touLinks>
						<div class="ps-3 mt-1">
							<a
								href="https://my.lifetime.life/policy/terms-of-use.html"
								target="_blank"
								rel="noopener noreferrer"
								type="button"
								class="link-underlined small text-nowrap me-1 btn-primary"
							>
								Terms of Use
							</a>
							<a
								href="https://my.lifetime.life/policy/privacy-policy.html "
								target="_blank"
								rel="noopener noreferrer"
								type="button"
								class="link-underlined small text-nowrap btn-primary"
							>
								Privacy Policy
							</a>
						</div>
					</template>
				</ContactForm>

				<div class="d-grid gap-2">
					<button
						v-adobe-data-layer="submitADLConfig"
						class="btn btn-dark"
						type="submit"
						:disabled="loading"
						data-testid="submit"
					>
						Confirm Your Booking
					</button>
				</div>
			</div>

			<div v-if="step === 'Confirm'" class="row justify-content-center" data-testid="confirm">
				<hr class="my-3" />
				<div class="col-8 text-center">
					<h3 class="h6">Club Tour</h3>
					<p class="lead">You're booked!</p>
					<p class="mb-8">
						We can't wait to show you the club. A confirmation email should be landing in your inbox
						right about now.
					</p>

					<a class="btn btn-outline-dark" :href="`/locations/clubs.go/${club.mmsClubId}`">
						Club Overview
					</a>
				</div>
			</div>

			<div v-show="step === 'Error'" class="error-wrapper" data-testid="error">
				<hr class="my-3" />
				<div class="error-header w-100" data-testid="error-header">{{ errorStateHeader }}</div>
				<div class="error-subheader w-100" data-testid="error-message">
					{{ errorStateMessage }}
				</div>
			</div>
		</form>
	</div>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate';
import { computed, onMounted, ref, watch, type Ref } from 'vue';
import ContactForm from 'Lib/components/shared/ContactForm.vue';
import DateSelector from 'Lib/components/schedule-a-tour/DateSelector.vue';
import BookingSummary from 'Lib/components/schedule-a-tour/BookingSummary.vue';
import TimeSlots from 'Lib/components/schedule-a-tour/TimeSlots.vue';
import EnhancedFields from 'Lib/components/lead-form/EnhancedFields.vue';
import { bookingsService } from '@/services/bookingsService';
import { club } from '@/util/club';
import { logError, logWarn } from '@/util/logger';
import { prerenderGateCheck } from '@/util/prerenderGateCheck';
import vAdobeDataLayer from '@/directives/AdobeDataLayer';
import type {
	AemBookingDay,
	ITimeSlot,
	ITourAuthoredContent,
	ITourFormData
} from 'Types/aemui/schedule-a-tour/scheduleATour';
import { featureFlagService, type ltFramework } from '@life-time-inc/enterprise-framework-core';
import { aemServletService } from '@/services/aemServletService';
import type {
	AemuiWaitlistInterest,
	AemuiWaitlistItem
} from 'Types/aemui/content-fragments/contentFragment';
import { leadFormService } from '@/services/leadFormService';
import type { IFormData } from 'Types/aemui/lead-form/aemuiLeadForm';
import { getAuthoredContent } from '@/util/getAuthoredContent';

const loading = ref(true);
const step: Ref<1 | 2 | 'Confirm' | 'Error'> = ref(1);
const days: Ref<AemBookingDay[]> = ref([]);
const selectedDay: Ref<AemBookingDay | null> = ref(null);
const selectedTimeslot: Ref<ITimeSlot | null> = ref(null);
const defaultDuration = ref('');
const errorStateHeader = ref("Something isn't right!");
const errorStateMessage = ref(
	"Our server encountered an error and couldn't process your request. Please hit refresh and try again."
);
const firstAvailableMsg = ref(false);
const clubData: Ref<ltFramework.IClubDetailsFromDOM> = ref(club);
const authoredContent: Ref<ITourAuthoredContent> = ref({});

const showEnhancedFields = featureFlagService.isEnabled('aemuiEnhancedTour');
const isEnhanced = computed(() => {
	return showEnhancedFields && authoredContent.value.enhancedTour === 'true';
});

const startingPrice: Ref<string | null> = ref(null);
const membershipTypes: Ref<AemuiWaitlistItem[]> = ref([]);
const interests: Ref<AemuiWaitlistInterest[]> = ref([]);

const consentText = `I acknowledge that I am at least 18 years of age and agree to the Privacy Policy and Terms of Use and expressly consent to Life Time's use of this information to contact me, which may include sending me electronic commercial or promotional messages, which I may opt out of at any time.`;

onMounted(async () => {
	if (prerenderGateCheck()) {
		return;
	}

	authoredContent.value = getAuthoredContent<ITourAuthoredContent>('aemuiEnhancedTourScript');

	try {
		days.value = await bookingsService.getStaffAvailability();
	} catch (error: unknown) {
		days.value = [] as AemBookingDay[];
		step.value = 'Error';
		logError('GET Staff Availability error', { clubId: club.mmsClubId }, error);
	} finally {
		loading.value = false;
	}

	const firstDayWithTimes = days.value.findIndex((t) => t?.timeSlots?.length > 0);
	selectedDay.value = days.value[firstDayWithTimes];
	firstAvailableMsg.value = firstDayWithTimes > 0;
	defaultDuration.value =
		days.value.find((x) => x?.timeSlots?.length > 0)?.timeSlots[0].duration ?? '';

	if (clubData.value.mmsClubId && isEnhanced.value) {
		try {
			const response = await aemServletService.getWaitlistItems(clubData.value.mmsClubId);
			const interestRes = await aemServletService.getWaitlistInterests(clubData.value.mmsClubId);
			startingPrice.value = response.data.club.startingPriceString;
			membershipTypes.value = response.data.variations;
			interests.value = interestRes.data.variations;
		} catch (error: unknown) {
			logError('GET waitlist items failed', { clubId: clubData.value.mmsClubId }, error);
		}
	}
});

watch(selectedDay, (newDay, oldDay) => {
	selectedTimeslot.value = null;

	if (newDay && oldDay) {
		firstAvailableMsg.value = false;
	}
});

const { handleSubmit } = useForm<{
	timeSlot: ITimeSlot;
	firstName: string;
	lastName: string;
	telephone2: string;
	emailAddress: string;
	tou: boolean;
}>({
	initialValues: {
		tou: false
	}
});

const onSubmit = handleSubmit(async (value) => {
	loading.value = true;
	let leadFormValues = {};
	const tourForm: ITourFormData = {
		firstName: value.firstName,
		lastName: value.lastName,
		email: value.emailAddress,
		phone: value.telephone2
	};
	try {
		await bookingsService.submitBooking(value.timeSlot, tourForm);
		if (isEnhanced.value) {
			leadFormValues = buildLeadForm(value);
			await leadFormService.submitForm(leadFormValues);
		}
		step.value = 'Confirm';
	} catch (error: unknown) {
		// Bookings doesn't give a unique code back
		if (error instanceof Error && error?.message.includes('Staff not available')) {
			errorStateHeader.value = 'Someone got there before you!';
			errorStateMessage.value =
				'The time slot you requested has just been booked by somebody else, please hit refresh to find another time.';
			logWarn('Submit booking double booked', { tourForm }, error);
		} else {
			if (isEnhanced.value) {
				logError('Enhanced Tour submit error', { leadFormValues }, error);
			}
			logError('Submit booking error', { tourForm }, error);
		}
		step.value = 'Error';
	} finally {
		loading.value = false;
	}
});

const submitADLConfig = {
	name: isEnhanced.value ? 'enhancedtour' : 'scheduletour',
	type: 'button',
	title: 'Confirm Booking'
};

const buildLeadForm = (values: IFormData) => {
	const url = new URL(window.location.href);

	return {
		addressLatitude: '',
		addressLongitude: '',
		birthYear: '',
		channel: 'AEM',
		clubStatus: '',
		clubType: 'club',
		communicationConsent: '',
		consentDateAndTime: document.lastModified,
		consentIPAddress: '',
		consentText: consentText,
		currentMember: '',
		dcmp: '',
		description: '',
		device: '',
		emailAddress: values.emailAddress,
		emailTemplate: '',
		firstName: values.firstName,
		gcId: '',
		gcLId: '',
		group: '',
		inquiryType: 'Open Waitlist',
		interests: values.interests ?? '',
		keyWords: '',
		landingPage: `${document.URL}`,
		lastName: values.lastName,
		leadType: 'Web',
		membershipInfoRequested: 'Wait List',
		membershipType: values.membershipType,
		mmsClubId: clubData.value.mmsClubId.toString(),
		numberOfAdults: values.numberOfAdults,
		numberOfJuniors: values.numberOfJuniors,
		operatingSystem: '',
		referringDomain: `${document.location.origin}`,
		referringPage: `${document.location.pathname}`,
		subject: '',
		telephone2: values.telephone2,
		tou: values.tou,
		utmAudience: url.searchParams.get('utmAudience') ?? '',
		utmCampaign: url.searchParams.get('utmCampaign') ?? '',
		utmContent: url.searchParams.get('utmContent') ?? '',
		utmImage: url.searchParams.get('utmImage') ?? '',
		utmMedium: url.searchParams.get('utmMedium') ?? '',
		utmSource: url.searchParams.get('utmSource') ?? '',
		utmTerm: url.searchParams.get('utmTerm') ?? '',
		visitCount: '',
		visitorId: ''
	};
};

defineExpose({
	defaultDuration,
	loading,
	selectedDay,
	selectedTimeslot,
	step,
	onSubmit
});
</script>

<style lang="scss" scoped>
.schedule-tour {
	.row {
		margin-bottom: 3rem;
	}

	.error-wrapper {
		margin-top: 7.5rem;
		margin-bottom: 7.5rem;
		margin-right: 2rem;
		margin-left: 2rem;
		width: 100%;
		height: 100%;
		flex-direction: column;
		justify-content: flex-start;
		align-items: center;
		gap: 2rem;
		display: inline-flex;
		text-align: center;
		color: var(--kpl-secondary);
		font-family: var(--kpl-font-sans-serif);
		font-weight: 400;
		word-wrap: break-word;
	}

	.error-header {
		font-size: 1.625rem;
		line-height: 2rem;
	}

	.error-subheader {
		font-size: 0.9rem;
		line-height: 1rem;
	}

	& :deep(.card) {
		background-color: var(--kpl-light);
	}
}
</style>
